import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendChatMessage } from '../lib/chatApi';

export default function ChatWidget() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);

  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'agent', content: t('chat.greeting') }]);
    }
  }, [open, messages.length, t]);

  useEffect(() => {
    if (open) {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
      inputRef.current?.focus();
    }
  }, [messages, open]);

  async function send(text) {
    const content = text.trim();
    if (!content || sending) return;

    setInput('');
    setMessages((m) => [...m, { role: 'user', content }]);
    setSending(true);

    try {
      const reply = await sendChatMessage(content);
      setMessages((m) => [
        ...m,
        {
          role: 'agent',
          content: reply.content || t('chat.empty'),
          suggestions: reply.suggestions,
        },
      ]);
    } catch (err) {
      const msg = err?.code === 'rate_limited' ? t('chat.rateLimited') : t('chat.error');
      setMessages((m) => [...m, { role: 'error', content: msg }]);
    } finally {
      setSending(false);
    }
  }

  function handleSend(e) {
    e.preventDefault();
    send(input);
  }

  // Suggested questions under the latest answer only; sending anything (a tap
  // or a typed message) replaces the reply they belong to, so they go.
  const last = messages[messages.length - 1];
  const suggestions =
    !sending && last?.role === 'agent' && last.suggestions?.length ? last.suggestions : [];

  const side = isRtl ? { left: '1rem' } : { right: '1rem' };

  return (
    <div
      className="fixed bottom-4 z-50 flex flex-col items-end"
      style={side}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      {open && (
        <div className="mb-3 flex h-[70vh] max-h-[calc(100dvh-6rem)] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl sm:h-[32rem] sm:max-h-[70vh] sm:w-[22rem]">
          <div className="flex items-center justify-between border-b border-border bg-surface-elevated px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-fg">
                <BotIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-medium text-text">{t('chat.title')}</p>
                <p className="text-xs text-success">{t('chat.online')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('chat.close')}
              className="rounded-md p-1 text-text-muted transition hover:bg-surface hover:text-text"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} />
            ))}
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {suggestions.map((q) => (
                  <button
                    key={q}
                    type="button"
                    dir="auto"
                    onClick={() => send(q)}
                    aria-label={t('chat.askChip', { question: q })}
                    className="max-w-full rounded-full border border-primary/40 bg-surface px-3 py-1.5 text-start text-xs font-medium text-primary transition hover:bg-primary/10"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-surface-elevated px-3 py-2 text-text-muted">
                  <TypingDots />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSend} className="flex items-center gap-2 border-t border-border p-3">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              // The API's hard ceiling. The real limit is set by the admin
              // (default 1,000) and enforced by the chat, which answers a
              // longer message with a polite "please shorten it".
              maxLength={4000}
              placeholder={t('chat.placeholder')}
              autoComplete="off"
              enterKeyHint="send"
              className="min-w-0 flex-1 rounded-lg border border-border bg-bg px-3 py-2 text-base text-text placeholder:text-text-faint focus:border-border-strong focus:outline-none"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              aria-label={t('chat.send')}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-fg transition hover:bg-primary-hover disabled:opacity-40"
            >
              <SendIcon className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? t('chat.close') : t('chat.open')}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-fg shadow-lg transition hover:bg-primary-hover"
      >
        {open ? <CloseIcon className="h-6 w-6" /> : <ChatIcon className="h-6 w-6" />}
      </button>
    </div>
  );
}

function Bubble({ role, content }) {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-primary px-3 py-2 text-sm text-primary-fg">
          {content}
        </div>
      </div>
    );
  }
  const tone =
    role === 'error'
      ? 'bg-surface-elevated text-danger'
      : 'bg-surface-elevated text-text';
  return (
    <div className="flex justify-start">
      <div className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm ${tone}`}>
        {content}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="flex gap-1">
      <span className="h-2 w-2 animate-bounce rounded-full bg-text-faint [animation-delay:-0.3s]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-text-faint [animation-delay:-0.15s]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-text-faint" />
    </span>
  );
}

function ChatIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function CloseIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
function SendIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}
function BotIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4M8 16h.01M16 16h.01" />
    </svg>
  );
}
